<template>
  <div class="mx-auto mb-12 mt-10 max-w-md lg:mb-0">
    <InfoBar
      v-if="loginChangePasswordData"
      variant="success"
      :title="t('common.success')"
      :message="t('common.passwordResetSuccessfully')"
    />
    <form v-else class="form-wrapper" @submit.prevent="onSubmit">
      <InputWrapped
        :label="t('input.label.newPassword')"
        :placeholder="t('input.label.newPassword')"
        name="password"
        type="password"
        v-model="password"
        v-bind="passwordAttrs"
        :error="errors.password"
        autocomplete="new-password"
        :disabled="isLoginChangePasswordFetching"
      />
      <InputWrapped
        :label="t('input.label.retypePassword')"
        :placeholder="t('input.label.retypePassword')"
        name="retypePassword"
        type="password"
        v-model="retypePassword"
        v-bind="retypePasswordAttrs"
        :error="errors.retypePassword"
        autocomplete="new-password"
        :disabled="isLoginChangePasswordFetching"
      />
      <Button
        type="submit"
        :loading="isLoginChangePasswordFetching"
        :disabled="isLoginChangePasswordFetching"
      >
        <Loader2
          v-if="isLoginChangePasswordFetching"
          class="mr-2 h-4 w-4 animate-spin stroke-white"
        />
        {{ t('common.resetPassword') }}
      </Button>
      <InfoBar
        v-if="loginChangePasswordError"
        variant="destructive"
        :title="t('common.error')"
        :message="loginChangePasswordError?.message"
        @close="loginChangePasswordError = null"
      />
    </form>
  </div>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';
import { useAccountStore } from '@stores/account.store';
import { storeToRefs } from 'pinia';

// Components
import InfoBar from '@generics/InfoBar.vue';
import InputWrapped from '@modules/InputWrapped/InputWrapped.vue';
import Button from '@ui/button/Button.vue';
import { useI18n } from 'vue-i18n';

// Icons
import { Loader2 } from 'lucide-vue-next';
import { watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';

interface ResetPasswordForm {
  password: string;
  retypePassword: string;
}

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const accountStore = useAccountStore();
const { loginChangePasswordData, loginChangePasswordError, isLoginChangePasswordFetching } =
  storeToRefs(accountStore);
const schema = toTypedSchema(
  yup.object({
    password: yup
      .string()
      .required(t('errors.required'))
      .min(8, t('errors.passwordMinLengthNotMet')),
    retypePassword: yup
      .string()
      .required(t('errors.required'))
      .oneOf([yup.ref('password')], t('errors.passwordsMustMatch')),
  }),
);
const { errors, handleSubmit, defineField, values } = useForm<ResetPasswordForm>({
  validationSchema: schema,
  validateOnMount: false,
  initialValues: {
    password: '',
    retypePassword: '',
  },
});

const [password, passwordAttrs] = defineField('password', {
  validateOnModelUpdate: false,
});
const [retypePassword, retypePasswordAttrs] = defineField('retypePassword', {
  validateOnModelUpdate: false,
  validateOnBlur: true,
});

const onSubmit = handleSubmit(async (values) => {
  route.meta.hideFooter = true;
  await accountStore.loginChangePassword(values.password);
  if (loginChangePasswordError.value) {
    route.meta.hideFooter = false;
    return;
  }
  setTimeout(() => {
    router.push({ name: 'Home' });
  }, 1000);
});

watch(
  values,
  () => {
    loginChangePasswordError.value = null;
    loginChangePasswordData.value = null;
  },
  { deep: true },
);
</script>
